import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Check, CookingPot } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useUoms } from "@/hooks/useUomReference";
import { ingredientLineName } from "@/types/recipe";
import type { LineMeasures } from "@/lib/api/requests/recipes";
import { LineAllergens } from "../AllergenSymbol";
import { StepPhotos } from "../StepPhotos";
import { CcpLegend, CcpStepFlag, CcpStepPrintNote } from "../ccp";
import { methodHasCcp } from "@/lib/recipes/ccp";
import { useBatchLabel } from "./useBatchLabel";
import {
  cardEquivalent,
  flowLayout,
  kitchenAmount,
  stepRangeLabel,
  type BatchScale,
  type HeldInflow,
  type MethodGroup,
  type NumberedStep,
  type StagedMethod,
} from "@/lib/recipes/staged-method";

interface RecipeSimpleCardProps {
  method: StagedMethod;
  scale: BatchScale;
  /** The yield at this scale ("32"), or null when the recipe has none. */
  yieldCount: string | null;
  /** Each line's weight and volume, by ingredient line id, from the cost summary. */
  measures?: Map<number, LineMeasures | null>;
  /** The recipe, for the "Got it" on a critical control point. */
  recipeId?: number;
  /** The cooking session the card was opened from, when it was. */
  cookingSessionId?: number;
}

/**
 * The simple recipe card: the tabular "Espresso Brownies" layout with the flow
 * grid's stages. Mise en place to tick off first, then a table read left to
 * right — each ingredient's box runs up to the stage it goes in at, each
 * stage's block spans everything it works on, and the last column is the
 * finished dish. Tapping a stage opens the full instructions for it.
 */
export function RecipeSimpleCard({ method, scale, yieldCount, measures, recipeId, cookingSessionId }: RecipeSimpleCardProps) {
  const { t } = useLingui();
  const { data: uoms } = useUoms();
  const [done, setDone] = useState<Set<number>>(new Set());
  const { columns, rows, spans, starts, heldInto } = flowLayout(method);
  const lastColumn = columns.length - 1;
  // Steps no stage claims have no block; they are listed after the card.
  const unstagedSteps = method.groups.filter((group) => group.stage === null).flatMap((group) => group.steps);
  const batchLabel = useBatchLabel(scale, yieldCount);
  const ccp = { recipeId, cookingSessionId };

  const amount = (lineId: number, quantity: number, unit: string) => {
    const written = kitchenAmount(Number(quantity) * scale, unit);
    const equivalent = cardEquivalent(Number(quantity), unit, measures?.get(lineId), scale, uoms);
    return equivalent ? `${written} (${equivalent})` : written;
  };

  const stepsLabel = (numbers: number[]) => {
    const range = stepRangeLabel(numbers);
    if (!range) return null;
    if ("one" in range) return t`Step ${range.one}`;
    if ("list" in range) {
      const list = range.list.join(", ");
      return t`Steps ${list}`;
    }
    return t`Steps ${range.from}–${range.to}`;
  };

  const tasks = method.tasks.map((task) => {
    const isDone = done.has(task.id);
    return (
      <button
        key={task.id}
        type="button"
        aria-pressed={isDone}
        onClick={() =>
          setDone((current) => {
            const next = new Set(current);
            if (next.has(task.id)) next.delete(task.id);
            else next.add(task.id);
            return next;
          })
        }
        className="flex items-center justify-center gap-3 rounded-xl bg-(--color-surface-muted) px-4 py-3 font-medium text-(--color-text-primary)"
      >
        <span
          className={cn(
            "flex size-5 items-center justify-center rounded border-2",
            isDone ? "border-(--color-surface-sidebar) bg-(--color-surface-sidebar) text-(--color-text-on-dark)" : "border-(--color-text-primary)",
          )}
        >
          {isDone && <Check className="size-3.5" />}
        </span>
        <span className={cn(isDone && "text-(--color-text-secondary) line-through")}>
          {task.action}
          {task.detail ? ` · ${task.detail}` : ""}
        </span>
      </button>
    );
  });

  const unstagedPanel =
    unstagedSteps.length > 0 ? (
      <div className="flex flex-col gap-2 rounded-2xl border border-dashed border-(--color-border) p-5 text-sm text-(--color-text-primary)">
        <span className="text-xs font-semibold tracking-wide text-(--color-text-secondary) uppercase">{t`Not staged yet`}</span>
        <StepList steps={unstagedSteps} ccp={ccp} />
      </div>
    ) : null;

  if (columns.length === 0) {
    return (
      <div className="flex flex-col gap-1.5 text-base">
        {tasks}
        <p className="rounded-xl bg-(--color-surface-muted) px-4 py-6 text-center text-sm text-(--color-text-secondary)">
          {t`The recipe card needs the method split into stages.`}
        </p>
        {unstagedPanel}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 text-base">
      {tasks}

      <div className="overflow-x-auto print:overflow-visible">
        <div
          className="grid min-w-[48rem] gap-1.5"
          style={{
            gridTemplateColumns: `minmax(16rem, 2.4fr) repeat(${columns.length}, minmax(5.5rem, 1fr))`,
            gridAutoRows: "minmax(2.75rem, auto)",
          }}
        >
          <div
            style={{ gridColumn: 1, gridRow: 1 }}
            className="self-end px-3 text-xs font-semibold tracking-wide text-(--color-text-secondary) uppercase"
          >
            {t`Ingredients`} · {batchLabel}
          </div>
          {columns.map((_, index) => (
            <div
              key={index}
              style={{ gridColumn: index + 2, gridRow: 1 }}
              className="self-end text-center text-xs font-semibold tracking-wide text-(--color-text-secondary) uppercase"
            >
              {t`Stage ${index + 1}`}
            </div>
          ))}

          {rows.map((row, index) => {
            // A line runs across the stages it skips, up to the one it goes in at.
            const to = row.joinsAt === null ? columns.length + 2 : row.joinsAt + 2;
            return (
              <div
                key={row.line.id}
                style={{ gridColumn: `1 / ${to}`, gridRow: index + 2 }}
                className={cn(
                  "flex items-center rounded-lg border border-(--color-border) px-3 py-2",
                  row.joinsAt === null && "border-dashed text-(--color-text-secondary)",
                )}
              >
                <span>
                  <span className="font-bold text-(--color-text-primary)">
                    {amount(row.line.id, row.line.quantity, row.line.unit)}
                  </span>{" "}
                  <span className="text-(--color-text-primary)">{ingredientLineName(row.line)}</span>
                  {row.line.preparationNotes && <span className="text-(--color-text-secondary)">, {row.line.preparationNotes}</span>}{" "}
                  <LineAllergens line={row.line} />
                </span>
              </div>
            );
          })}

          {columns.map((group, index) => (
            <StageBlock
              key={group.stage!.id}
              group={group}
              index={index}
              total={columns.length}
              isLast={index === lastColumn}
              stepsLabel={stepsLabel(group.stage!.stepNumbers)}
              held={heldInto[index]!}
              ccp={ccp}
              style={{ gridColumn: index + 2, gridRow: `${starts[index]! + 2} / ${starts[index]! + spans[index]! + 2}` }}
            />
          ))}
        </div>
      </div>

      {methodHasCcp(method) && <CcpLegend />}

      {/* The instructions live behind a tap on screen; on paper there is
          nothing to tap, so the printed card lists them under the table. */}
      <div className="hidden flex-col gap-3 pt-3 text-sm text-(--color-text-primary) print:flex">
        {columns.map((group) => (
          <div key={group.stage!.id} className="flex flex-col gap-1">
            <span className="font-bold text-(--color-text-primary)">
              {group.stage!.action}
              {group.stage!.detail && <span className="font-normal text-(--color-text-secondary)"> · {group.stage!.detail}</span>}
            </span>
            <StepList steps={group.steps} ccp={ccp} stageName={group.stage!.action} />
          </div>
        ))}
      </div>
      {unstagedPanel}
    </div>
  );
}

interface StageBlockProps {
  group: MethodGroup;
  index: number;
  total: number;
  isLast: boolean;
  stepsLabel: string | null;
  /** Held outcomes added at this stage. */
  held: HeldInflow[];
  ccp: CcpTarget;
  style: React.CSSProperties;
}

/** One stage's block, which opens the full instructions for that stage. */
function StageBlock({ group, index, total, isLast, stepsLabel, held, ccp, style }: StageBlockProps) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const stage = group.stage!;
  const flagged = group.steps.filter((step) => step.isCcp);

  const block = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // With a warning icon over it, the block sits in a wrapper that takes the grid cell.
          style={flagged.length > 0 ? undefined : style}
          aria-label={t`${stage.action}: show instructions`}
          className={cn(
            "flex flex-col items-center justify-center gap-1 rounded-lg px-2 py-3 text-center transition-colors focus-visible:ring-2 focus-visible:ring-(--color-text-primary) focus-visible:outline-none",
            isLast
              ? "bg-(--color-surface-sidebar) text-(--color-text-on-dark)"
              : "bg-(--color-surface-muted) text-(--color-text-primary) hover:bg-(--color-border)",
            open && !isLast && "ring-2 ring-(--color-text-primary)",
          )}
        >
          {isLast && <CookingPot className="size-6" />}
          <span className="text-lg font-bold">{stage.action}</span>
          {stage.detail && (
            <span className={cn("font-semibold", isLast ? "text-xl" : "text-sm text-(--color-text-secondary)")}>
              {stage.detail}
            </span>
          )}
          {held.map((inflow) => (
            <span
              key={inflow.stage.id}
              className={cn("text-xs font-semibold", isLast ? "text-(--color-text-on-dark)/75" : "text-(--color-text-secondary)")}
            >
              {t`Add when ready: ${inflow.stage.action}`}
            </span>
          ))}
          {stepsLabel && (
            <span
              className={cn(
                "text-[0.7rem] font-semibold tracking-wide uppercase print:hidden",
                isLast ? "text-(--color-text-on-dark)/75" : "text-(--color-text-secondary)",
              )}
            >
              {stepsLabel}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        collisionPadding={16}
        className="flex w-[24rem] max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-2xl border-none bg-(--color-surface-sidebar) p-4 text-(--color-text-on-dark)"
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-semibold tracking-wide text-(--color-text-on-dark)/70 uppercase">
            {t`Stage ${index + 1} of ${total}`}
          </span>
          <span className="text-xl font-bold">{stage.action}</span>
          {stage.detail && <span className="text-sm text-(--color-text-on-dark)/80">{stage.detail}</span>}
        </div>
        {group.steps.length === 0 ? (
          <span className="text-sm text-(--color-text-on-dark)/75">{t`No steps linked to this stage.`}</span>
        ) : (
          <StepList steps={group.steps} ccp={ccp} stageName={stage.action} />
        )}
      </PopoverContent>
    </Popover>
  );

  if (flagged.length === 0) return block;

  // The icon is a sibling of the block's button, never inside it: a button in a button is not valid.
  return (
    <div style={style} className="relative grid">
      {block}
      <div className="absolute top-1.5 right-1.5 flex gap-1 print:hidden">
        {flagged.map((step) => (
          <CcpStepFlag
            key={step.number}
            recipeId={ccp.recipeId}
            cookingSessionId={ccp.cookingSessionId}
            number={step.number}
            text={step.text}
            stageName={stage.action}
          />
        ))}
      </div>
    </div>
  );
}

interface CcpTarget {
  recipeId?: number;
  cookingSessionId?: number;
}

function StepList({ steps, ccp, stageName }: { steps: NumberedStep[]; ccp: CcpTarget; stageName?: string }) {
  return (
    <ol className="flex flex-col gap-2 text-sm leading-snug">
      {steps.map((step) => (
        <li key={step.number} className="flex gap-2">
          <span className="font-bold">{step.number}.</span>
          <span className="flex min-w-0 flex-col">
            {step.isCcp ? (
              <span className="flex items-start gap-2">
                <span>{step.text}</span>
                <CcpStepFlag recipeId={ccp.recipeId} cookingSessionId={ccp.cookingSessionId} number={step.number} text={step.text} stageName={stageName} />
              </span>
            ) : (
              <span>{step.text}</span>
            )}
            <StepPhotos urls={step.mediaUrls} />
            {step.isCcp && <CcpStepPrintNote text={step.text} className="mt-1" />}
          </span>
        </li>
      ))}
    </ol>
  );
}
